import { type ReactNode, useState } from "react";
import { Pencil, Plane, Plus, Save, Trash2, X } from "lucide-react";
import type { Aircraft, AircraftStatus, IlsCategory, SaveGame, WorldData } from "../../../domain/types";
import { defaultAircraftCapabilities, formatIlsCategory } from "../../../domain/rules/aircraft";
import { formatAircraftStatus } from "../../format";
import { AirportSearch } from "../../components/AirportSearch";
import { Select } from "../../components/Panel";
import { FilterAll } from "./gmTypes";
import { MiniStat, aircraftDraftFromAircraft, blankAircraftDraft, matchesQuery, type AircraftDraft } from "./GmShared";


export function GmAircraftPanel({
  save,
  world,
  onSaveAircraft,
  onDeleteAircraft
}: {
  save: SaveGame | null;
  world: WorldData;
  onSaveAircraft: (input: Omit<Aircraft, "id"> & { id?: string }) => void;
  onDeleteAircraft: (aircraftId: string) => void;
}) {
  const [draft, setDraft] = useState<AircraftDraft>(() => blankAircraftDraft(save, world));
  const [fleetQuery, setFleetQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<FilterAll<AircraftStatus>>("all");
  const editing = Boolean(draft.id);
  const resetDraft = () => setDraft(blankAircraftDraft(save, world));
  const selectedType = world.aircraftTypes.find((type) => type.id === draft.typeId);
  const typeDefaults = defaultAircraftCapabilities(selectedType);
  const fleet = save?.fleet ?? [];
  const filteredFleet = fleet.filter((aircraft) => {
    return (
      (statusFilter === "all" || aircraft.status === statusFilter) &&
      matchesQuery([aircraft.registration, aircraft.typeId, aircraft.location, formatAircraftStatus(aircraft.status), aircraft.etopsMinutes, aircraft.maxTakeoffWeightKg], fleetQuery)
    );
  });
  const availableCount = fleet.filter((aircraft) => aircraft.status === "available").length;
  const blockedCount = fleet.filter((aircraft) => aircraft.status === "maintenance" || aircraft.status === "grounded").length;
  const restoreTypeDefaults = () =>
    setDraft((current) => ({
      ...current,
      etopsMinutes: typeDefaults.etopsMinutes,
      maxTakeoffWeightKg: typeDefaults.maxTakeoffWeightKg,
      ilsCategory: typeDefaults.ilsCategory,
      fuelOnBoardKg: Math.round((selectedType?.maxFuelKg ?? 18_000) * 0.5)
    }));
  const submit = () => {
    onSaveAircraft({
      ...draft,
      registration: draft.registration.trim().toUpperCase(),
      location: draft.location.trim().toUpperCase(),
      etopsMinutes: Number(draft.etopsMinutes) || typeDefaults.etopsMinutes,
      maxTakeoffWeightKg: Number(draft.maxTakeoffWeightKg) || typeDefaults.maxTakeoffWeightKg,
      fuelOnBoardKg: Number(draft.fuelOnBoardKg) || 0,
      hoursSinceMaintenance: Number(draft.hoursSinceMaintenance) || 0,
      cyclesSinceCheck: Number(draft.cyclesSinceCheck) || 0,
      availableFromDay: Number(draft.availableFromDay) || 1
    });
  };

  if (!save) {
    return <div className="empty-state">当前 GM 席位还没有航司存档，先创建航司后才能配置飞机资料。</div>;
  }

  return (
    <div className="gm-aircraft-program">
      <div className="gm-kpi-strip">
        <MiniStat label="机队总数" value={fleet.length} />
        <MiniStat label="可用飞机" value={availableCount} tone={availableFleetTone(availableCount)} />
        <MiniStat label="飞行中" value={fleet.filter((aircraft) => aircraft.status === "in_flight").length} />
        <MiniStat label="维修/停场" value={blockedCount} tone={blockedFleetTone(blockedCount)} />
      </div>

      <div className="gm-aircraft-form">
        <label>
          注册号
          <input value={draft.registration} onChange={(event) => setDraft((current) => ({ ...current, registration: event.target.value.toUpperCase() }))} />
        </label>
        <Select
          label="机型"
          value={draft.typeId}
          values={world.aircraftTypes.map((type) => type.id)}
          formatValue={(value) => {
            const type = world.aircraftTypes.find((item) => item.id === value);
            return aircraftTypeSelectLabel(type, value);
          }}
          onChange={(typeId) => {
            const type = world.aircraftTypes.find((item) => item.id === typeId);
            const defaults = defaultAircraftCapabilities(type);
            setDraft((current) => ({ ...current, typeId, etopsMinutes: defaults.etopsMinutes, maxTakeoffWeightKg: defaults.maxTakeoffWeightKg, ilsCategory: defaults.ilsCategory, fuelOnBoardKg: Math.round((type?.maxFuelKg ?? 18_000) * 0.5) }));
          }}
        />
        <Select
          label="状态"
          value={draft.status}
          values={["available", "in_flight", "maintenance", "grounded"]}
          formatValue={(value) => formatAircraftStatus(value as AircraftStatus)}
          onChange={(status) => setDraft((current) => ({ ...current, status: status as AircraftStatus }))}
        />
        <AirportSearch id="gm-aircraft-location" label="当前位置" world={world} value={draft.location} onChange={(location) => setDraft((current) => ({ ...current, location }))} />
        <label>
          ETOPS 分钟
          <input type="number" min={0} value={draft.etopsMinutes} onChange={(event) => setDraft((current) => ({ ...current, etopsMinutes: Number(event.target.value) }))} />
        </label>
        <label>
          放行 MTOW kg
          <input type="number" min={1} step={1000} value={draft.maxTakeoffWeightKg} onChange={(event) => setDraft((current) => ({ ...current, maxTakeoffWeightKg: Number(event.target.value) }))} />
        </label>
        <label>
          当前剩油 kg
          <input type="number" min={0} step={500} value={draft.fuelOnBoardKg} onChange={(event) => setDraft((current) => ({ ...current, fuelOnBoardKg: Number(event.target.value) }))} />
        </label>
        <Select
          label="盲降类别"
          value={draft.ilsCategory}
          values={["cat1", "cat2", "cat3"]}
          formatValue={(value) => formatIlsCategory(value as IlsCategory)}
          onChange={(ilsCategory) => setDraft((current) => ({ ...current, ilsCategory: ilsCategory as IlsCategory }))}
        />
        <label>
          维修小时
          <input type="number" min={0} value={draft.hoursSinceMaintenance} onChange={(event) => setDraft((current) => ({ ...current, hoursSinceMaintenance: Number(event.target.value) }))} />
        </label>
        <label>
          循环数
          <input type="number" min={0} value={draft.cyclesSinceCheck} onChange={(event) => setDraft((current) => ({ ...current, cyclesSinceCheck: Number(event.target.value) }))} />
        </label>
        <label>
          可用运营日
          <input type="number" min={1} value={draft.availableFromDay} onChange={(event) => setDraft((current) => ({ ...current, availableFromDay: Number(event.target.value) }))} />
        </label>
        <div className="gm-aircraft-actions">
          <button className="primary-command" disabled={!draft.registration.trim()} onClick={submit}>
            {aircraftSubmitIcon(editing)}
            {aircraftSubmitLabel(editing)}
          </button>
          <button className="command" onClick={restoreTypeDefaults}>
            恢复默认能力
          </button>
          <button className="command" disabled={!editing} onClick={resetDraft}>
            <X size={14} />
            新建
          </button>
        </div>
      </div>

      <div className="gm-filter-bar">
        <label>
          机队筛选
          <input value={fleetQuery} onChange={(event) => setFleetQuery(event.target.value)} placeholder="注册号/机型/位置/状态" />
        </label>
        <label>
          状态
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as FilterAll<AircraftStatus>)}>
            <option value="all">全部</option>
            <option value="available">可用</option>
            <option value="in_flight">飞行中</option>
            <option value="maintenance">维修中</option>
            <option value="grounded">停场</option>
          </select>
        </label>
      </div>

      <div className="table-wrap compact">
        <table>
          <thead>
            <tr>
              <th>飞机</th>
              <th>机型</th>
              <th>位置</th>
              <th>能力</th>
              <th>状态</th>
              <th>维修</th>
              <th>可用日</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredFleet.map((aircraft) => (
              <tr key={aircraft.id} className={aircraftRowClassName(draft.id, aircraft.id)}>
                <td>{aircraft.registration}</td>
                <td>{aircraft.typeId.toUpperCase()}</td>
                <td>{aircraft.location}</td>
                <td>{aircraftCapabilityLabel(aircraft)}</td>
                <td>
                  <span className={`status-pill ${aircraftStatusTone(aircraft.status)}`}>{formatAircraftStatus(aircraft.status)}</span>
                </td>
                <td>{Math.round(aircraft.hoursSinceMaintenance)}h / {aircraft.cyclesSinceCheck}cy</td>
                <td>D{aircraft.availableFromDay}</td>
                <td>
                  <div className="row-actions">
                    <button onClick={() => setDraft(aircraftDraftFromAircraft(aircraft))}>
                      <Pencil size={12} />
                      编辑
                    </button>
                    <button disabled={Boolean(save.activeOpsDay)} title={deleteAircraftTitle(save.activeOpsDay)} onClick={() => onDeleteAircraft(aircraft.id)}>
                      <Trash2 size={12} />
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredFleet.length === 0 && (
              <tr>
                <td colSpan={8}>没有匹配的飞机资料。</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type MiniStatTone = "neutral" | "good" | "warn" | "bad";

function availableFleetTone(availableCount: number): MiniStatTone {
  if (availableCount > 0) {
    return "good";
  }
  return "bad";
}

function blockedFleetTone(blockedCount: number): MiniStatTone {
  if (blockedCount > 0) {
    return "warn";
  }
  return "good";
}

function aircraftTypeSelectLabel(type: WorldData["aircraftTypes"][number] | undefined, fallback: string): string {
  if (type) {
    return `${type.name}（${type.id.toUpperCase()}）`;
  }
  return fallback;
}

function aircraftSubmitIcon(editing: boolean): ReactNode {
  if (editing) {
    return <Pencil size={15}/>;
  }
  return <Plus size={15}/>;
}

function aircraftSubmitLabel(editing: boolean): string {
  if (editing) {
    return "更新飞机";
  }
  return "新增飞机";
}

function aircraftRowClassName(activeDraftId: string | undefined, aircraftId: string): string {
  if (activeDraftId === aircraftId) {
    return "selected-row";
  }
  return "";
}

function aircraftCapabilityLabel(aircraft: Aircraft): string {
  const maxTakeoffWeightTons = Math.round(aircraft.maxTakeoffWeightKg / 1000);
  const fuelOnBoardTons = Math.round(aircraft.fuelOnBoardKg / 1000);
  const ilsCategory = formatIlsCategory(aircraft.ilsCategory);
  return `ETOPS ${aircraft.etopsMinutes} / ${maxTakeoffWeightTons}t / FOB ${fuelOnBoardTons}t / ${ilsCategory}`;
}

function deleteAircraftTitle(activeOpsDay: SaveGame["activeOpsDay"]): string {
  if (activeOpsDay) {
    return "运行日开放时不能删除飞机";
  }
  return "删除飞机";
}

function aircraftStatusTone(status: AircraftStatus): string {
  if (status === "available") {
    return "good";
  }
  if (status === "in_flight") {
    return "muted";
  }
  return "bad";
}
